/**
 * SkillDelivery Platform
 * ContactCard — public /q/:handle page (Release 12 Step 2).
 *
 * Fully public, no auth. Fetches /api/public/q/:handle and renders only
 * the fields the owner opted in to publish. Two actions:
 *   - "Save contact" (downloads .vcf from /api/public/q/:handle/vcard)
 *   - "Connect on SkillDelivery" (ABSOLUTE link to the main app so
 *     login + session cookies live on one host — see note below).
 *
 * Mobile-first layout, noindex meta tag, no analytics beyond the server
 * audit row that /q/:handle writes on fetch.
 *
 * CROSS-DOMAIN NOTE: this page is served from BOTH `app.skilldelivery.co.in`
 * (the main authenticated host) AND `connect.skilldelivery.co.in` (the
 * public share host). Browser sessions and sign-up state are
 * per-origin, so EVERY login / register / dashboard link must be an
 * ABSOLUTE URL to the main app host — never a relative `<Link>` — so a
 * visitor who scanned the QR into connect.* lands on the right origin
 * to pick up or create their session.
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Mail, Phone, Globe, Linkedin, UserPlus, Download } from 'lucide-react';

const API_BASE = (() => {
  const raw = process.env.REACT_APP_API_URL || 'https://skilldelivery-platform.onrender.com/api';
  return raw.endsWith('/api') ? raw : `${raw.replace(/\/$/, '')}/api`;
})();

// Authenticated-side origin. Logins and sign-ups happen on THIS host so
// cookies/sessions line up across the app. Overridable via env for
// staging; defaults to prod.
const APP_ORIGIN = (process.env.REACT_APP_PRIMARY_ORIGIN || 'https://app.skilldelivery.co.in').replace(/\/+$/, '');

const CYAN = '#06b6d4';
const CYAN_DARK = '#0e7490';

export default function ContactCard() {
  const { handle } = useParams();
  const [state, setState] = useState({ loading: true });

  // noindex, nofollow — this is a personal share link, not SEO.
  useEffect(() => {
    const prev = document.querySelector('meta[name="robots"]');
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    const title = document.title;
    return () => {
      document.head.removeChild(meta);
      if (prev) document.head.appendChild(prev);
      document.title = title;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/public/q/${encodeURIComponent(handle || '')}`, { cache: 'no-store' });
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.status === 404 || !res.ok || !body.success) {
          setState({ loading: false, notFound: true });
          return;
        }
        const data = body.data || {};
        if (data.fullName) document.title = `${data.fullName} — SkillDelivery`;
        setState({ loading: false, card: data });
      } catch (e) {
        if (!cancelled) setState({ loading: false, notFound: true });
      }
    })();
    return () => { cancelled = true; };
  }, [handle]);

  if (state.loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <p style={{ color: '#64748b', margin: 0 }}>Loading...</p>
        </div>
      </div>
    );
  }
  if (state.notFound) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h1 style={styles.heading}>Contact not found</h1>
          <p style={{ color: '#64748b' }}>This card isn't available right now.</p>
          <a href="https://skilldelivery.co.in" style={styles.secondaryLink} rel="noopener noreferrer">Back to SkillDelivery</a>
        </div>
      </div>
    );
  }

  const c = state.card || {};
  const vcardUrl = `${API_BASE}/public/q/${encodeURIComponent(handle)}/vcard`;
  const initials = (c.fullName || '')
    .split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase() || 'SD';

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.avatar}>{initials}</div>
          {c.fullName && <h1 style={styles.name}>{c.fullName}</h1>}
          {(c.title || c.organisation) && (
            <p style={styles.sub}>
              {[c.title, c.organisation].filter(Boolean).join(' - ')}
            </p>
          )}
        </div>

        <ul style={styles.list}>
          {c.email && (
            <li style={styles.row}>
              <Mail size={18} color={CYAN_DARK} />
              <a href={`mailto:${c.email}`} style={styles.rowLink}>{c.email}</a>
            </li>
          )}
          {c.phone && (
            <li style={styles.row}>
              <Phone size={18} color={CYAN_DARK} />
              <a href={`tel:${c.phone.replace(/\s+/g, '')}`} style={styles.rowLink}>{c.phone}</a>
            </li>
          )}
          {c.website && (
            <li style={styles.row}>
              <Globe size={18} color={CYAN_DARK} />
              <a href={c.website.startsWith('http') ? c.website : `https://${c.website}`}
                 target="_blank" rel="noopener noreferrer" style={styles.rowLink}>
                {c.website.replace(/^https?:\/\//i, '')}
              </a>
            </li>
          )}
          {c.linkedinUrl && (
            <li style={styles.row}>
              <Linkedin size={18} color={CYAN_DARK} />
              <a href={c.linkedinUrl} target="_blank" rel="noopener noreferrer" style={styles.rowLink}>LinkedIn</a>
            </li>
          )}
        </ul>

        <a href={vcardUrl} download style={styles.primaryBtn}>
          <Download size={16} style={{ marginRight: 8 }} />
          Save contact
        </a>

        {/* ABSOLUTE link to the authenticated host. Must NOT be a relative
            react-router Link — this page also lives on connect.* where
            there is no auth session. See CROSS-DOMAIN NOTE at the top. */}
        <a href={`${APP_ORIGIN}/register`} style={styles.secondaryBtn} rel="noopener noreferrer">
          <UserPlus size={16} style={{ marginRight: 8 }} />
          Connect on SkillDelivery
        </a>

        <p style={styles.footer}>
          Shared via <a href="https://skilldelivery.co.in" style={styles.footerLink} rel="noopener noreferrer">SkillDelivery</a>
        </p>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #f0f9ff 0%, #ffffff 100%)',
    padding: '32px 16px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    fontFamily: "'Segoe UI', Tahoma, sans-serif"
  },
  card: {
    width: '100%',
    maxWidth: 420,
    background: 'white',
    borderRadius: 16,
    boxShadow: '0 6px 24px rgba(2, 132, 199, 0.08)',
    padding: '28px 24px',
    boxSizing: 'border-box'
  },
  header: { textAlign: 'center', marginBottom: 20 },
  avatar: {
    width: 72, height: 72, borderRadius: '50%',
    background: `linear-gradient(135deg, ${CYAN} 0%, ${CYAN_DARK} 100%)`,
    color: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 24, fontWeight: 700, margin: '0 auto 12px', letterSpacing: 1
  },
  name: { margin: 0, fontSize: 22, color: '#0f172a', fontWeight: 700 },
  sub: { margin: '4px 0 0', color: '#64748b', fontSize: 14 },
  heading: { margin: 0, fontSize: 20, color: '#0f172a' },
  list: { listStyle: 'none', padding: 0, margin: '18px 0 24px' },
  row: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 0', borderTop: '1px solid #e2e8f0'
  },
  rowLink: { color: '#0f172a', textDecoration: 'none', fontSize: 15, wordBreak: 'break-all' },
  primaryBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: '100%', boxSizing: 'border-box',
    padding: '12px 18px', borderRadius: 10,
    background: `linear-gradient(135deg, ${CYAN} 0%, ${CYAN_DARK} 100%)`,
    color: 'white', fontWeight: 600, textDecoration: 'none', fontSize: 15
  },
  secondaryBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: '100%', boxSizing: 'border-box',
    marginTop: 10,
    padding: '12px 18px', borderRadius: 10,
    background: 'white', color: CYAN_DARK, border: `1px solid ${CYAN}`,
    fontWeight: 600, textDecoration: 'none', fontSize: 15
  },
  secondaryLink: { color: CYAN_DARK, fontSize: 14, marginTop: 12, display: 'inline-block' },
  footer: { textAlign: 'center', fontSize: 12, color: '#94a3b8', marginTop: 20 },
  footerLink: { color: CYAN_DARK }
};
